import React from 'react';
import { useParams, Link, useLocation, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPrint } from '@fortawesome/free-solid-svg-icons';

const OrderDetails = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const order = location.state?.order;

  const formatTimestamp = (timestamp) => {
    const d = new Date(timestamp * 1000);
    const s = ["th", "st", "nd", "rd"];
    const n = d.getDate();
    const o = s[(n % 10 > 3 || Math.floor(n % 100 / 10) === 1) ? 0 : n % 10];
    return `${n}${o} ${d.toLocaleString('en-GB', { month: 'short', year: 'numeric' })} ${d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  };

  const handlePrint = () => {
    window.print();
  };

  if (!order) {
    return <Navigate to="/admin/orders" replace />;
  }


  return (
    <div className="p-4 max-w-md mx-auto">
      {/* Header - Hide when printing */}
      <div className="print:hidden flex justify-between items-center mb-4">
        <Link
          to="/admin/orders"
          className="text-gray-600 hover:text-gray-900 flex items-center"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Orders
        </Link>
        <button
          onClick={handlePrint}
          className="bg-primary text-white px-4 py-2 rounded-lg flex items-center"
        >
          <FontAwesomeIcon icon={faPrint} className="mr-2" />
          Print Receipt
        </button>
      </div>

      {/* Receipt Content */}
      <div className="bg-white font-mono p-2 print:shadow-none print:max-w-[80mm] print:!m-0 print:!p-0">
        
        {/* Restaurant Info */}
        <div className="text-center mb-2">
          <h1 className="text-xl font-bold">{order.restaurant_name || 'Restaurant Name'}</h1>
          <p className="text-xs">{order.restaurant_street}</p>
          <p className="text-xs">{order.restaurant_city}, {order.restaurant_state} {order.restaurant_zipcode}</p>
          <p className="text-xs">Phone: {order.restaurant_phone}</p>
        </div>

        <div className="border-t border-b border-gray-200 py-1 mb-2">
          <div className="grid grid-cols-2 gap-1">
            <div>
              <h2 className="font-semibold text-sm">Order Info</h2>
              <p className="text-xs">Order #: {order.id}</p>
              <p className="text-xs">Date: {formatTimestamp(order.accepted_at.seconds)}</p>
              <p className="text-xs">Type: {order.type?.toUpperCase()}</p>
              <p className="text-xs">Status: {order.status?.toUpperCase()}</p>
            </div>
            <div>
              <h2 className="font-semibold text-sm">Customer Info</h2>
              <p className="text-xs">{order.client_first_name} {order.client_last_name}</p>
              <p className="text-xs">{order.client_email}</p>
              <p className="text-xs">{order.client_phone}</p>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="mb-2">
          <h2 className="font-semibold text-sm mb-1 border-b pb-1">Order Items</h2>
          <div className="space-y-1">
            {order.items?.map((item, index) => (
              <div key={index} className="border-b border-dotted pb-1">
                <div className="flex justify-between font-medium">
                  <span className="text-xs">{item.name} × {item.quantity}</span>
                  <span className="text-xs">${Number(item.total_item_price || 0).toFixed(2)}</span>
                </div>
                {item.options?.length > 0 && (
                  <div className="text-xs text-gray-600 ml-1 mt-0.5">
                    {item.options.map((option, idx) => (
                      <p key={idx}>{option.group_name}: {option.name}</p>
                    ))}
                  </div>
                )}
                {item.instructions && (
                  <p className="text-xs text-gray-600 ml-1 mt-0.5">
                    Note: {item.instructions}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary */}
        <div className="border-t pt-1">
          <div className="flex justify-between mb-0.5">
            <span className="text-sm">Subtotal</span>
            <span className="text-sm">${Number(order.sub_total_price || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between mb-0.5">
            <span className="text-sm">Tax</span>
            <span className="text-sm">${Number(order.tax_value || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold text-base">
            <span className="text-sm">Total</span>
            <span className="text-sm">${Number(order.total_price || 0).toFixed(2)}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-600 text-xs mt-2">
          <p>Thank you for your order!</p>
          <p className="print:hidden">Generated on {formatTimestamp(Math.floor(Date.now() / 1000))}</p>
        </div>
        
      </div>
    </div>
  );
};

export default OrderDetails;